import React, { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import Loader from "../../components/loader";
import { LayoutForNoting } from "../../components/layout";
import { siteMode } from "../../cores/siteMode";

import Loadable from "@react-loadable/revised";
import compareVersions, { VERSION_CM_5112_PROJECT_SITE_ACCESS, VERSION_CM_5214_ADMIN } from "../../cores/version";
import menuService from "@services/menuService";
import Stage from "@routes/stage/Stage";

import { setStage } from "@modules/stage";
import { setProject } from "@modules/project";
import { getStagesProjects, getUser } from "@modules/user";
import { getProjectSettings, getStageSettings } from "@modules/settings";

import { sessionKeeper } from "../../cores/sessionKeeper";
import InvalidUser from "@routes/errors/InvalidUser";

const LoadableTokenExpiredModal = Loadable({
    loader: () => import("../../components/modals/TokenExpired"),
    loading: () => null,
});

const User = ({ children, ...props }) => {
    const { t, i18n } = useTranslation();

    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const user = useSelector(({ user }) => user);
    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const token = useSelector(({ token }) => token);

    const [noSiteAccess, setNoSiteAccess] = useState({ key: null, value: false, errorType: null });

    const NO_SITE_ACCESS_KEY = {
        NO_SITE_ACCESS_1: "NO_SITE_ACCESS_1",
        NO_SITE_ACCESS_2: "NO_SITE_ACCESS_2",
        NO_SITE_ACCESS_3: "NO_SITE_ACCESS_3",
        NO_SITE_ACCESS_4: "NO_SITE_ACCESS_4",
        NO_PERMISSION_TO_VIEW_PAGE_1: "NO_PERMISSION_TO_VIEW_PAGE_1",
        NO_PERMISSION_TO_VIEW_PAGE_2: "NO_PERMISSION_TO_VIEW_PAGE_2",
    };
    const closeNoAccessPopup = () => {
        history.replace("/spaces");
    };

    const updateHeaderTheme = (headerTheme, stageId, projectId) => {
        if (headerTheme && headerTheme.value) {
            if (projectId) {
                localStorage.setItem(
                    "projectHeaderTheme",
                    JSON.stringify({
                        stageId,
                        projectId,
                        isOverridable: headerTheme.isOverridable,
                        data: JSON.parse(headerTheme.value),
                    }),
                );
            } else {
                localStorage.setItem(
                    "headerTheme",
                    JSON.stringify({
                        stageId,
                        isOverridable: headerTheme.isOverridable,
                        data: JSON.parse(headerTheme.value),
                    }),
                );
            }
        }
    };

    const isAdminSetting = useMemo(() => {
        if (!location.pathname) return false;

        return location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1 && !projectId;
    }, [location]);

    const updateSettings = async (stage) => {
        const settings = await dispatch(
            getStageSettings({
                stageId: stage.id,
                apiEndpoint: stage.endpoint,
            }),
        );
        updateHeaderTheme(settings?.General?.headerTheme, stage.id, null);
        const consoleDefaultListLayout = settings?.General?.consoleDefaultListLayout?.value;
        const originGeneralSetting = JSON.parse(localStorage.getItem("generalSetting"));

        if (consoleDefaultListLayout) {
            localStorage.setItem(
                "generalSetting",
                JSON.stringify({ ...originGeneralSetting, consoleDefaultListLayout }),
            );
        }
    };

    const updateProjectSettings = async (stage, project, resource) => {
        const settings = await dispatch(
            getProjectSettings({
                stageId: stage.id,
                apiEndpoint: stage.endpoint,
                projectId: project.id,
                params: {
                    resource,
                },
            }),
        );

        if (settings && resource === "General") {
            updateHeaderTheme(settings?.General?.headerTheme, stage.id, project.id);
        }
    };

    useEffect(() => {
        if (!token?.value) return;
        if (!user.data) {
            dispatch(getUser());
        }
        if (!user.stages.data) {
            dispatch(getStagesProjects());
        }
    }, [token.value]);

    //stage, project 설정
    useEffect(() => {
        const locationPathname = window.location.pathname;
        if (["/library/spaces", "/spaces", "/access-token-bridge", "/snsLogin/youtube"].indexOf(locationPathname) !== -1) return; //space목록에서는 space권한체크 불필요함
        if (!user.data || !user.stages?.data || user.stages.allProjectsPending) return;

        let spaceKey = spaceId;
        let projectKey = projectId;
        if (user.stages.data.length === 1 && user.stages.data[0]?.projects?.length === 1) {
            dispatch(setStage(user.stages.data[0]));
            dispatch(setProject(user.stages.data[0].projects[0]));
            updateSettings(user.stages.data[0]);
        } else if (user.stages.data.length >= 1) {
            if (location.pathname === `/` || location.pathname === "/library/") {
                const currentSession = sessionKeeper.get();
                if (currentSession) {
                    if (
                        !currentSession.stageId ||
                        !currentSession.projectId ||
                        (user &&
                            user.stages &&
                            user.stages.data &&
                            user.stages.data.length > 0 &&
                            user.stages.data.filter((stg) => stg.id === currentSession.stageId).length === 0)
                    ) {
                        history.replace(`${location.pathname}spaces`);
                        return;
                    }

                    spaceKey = currentSession.stageId;
                    projectKey = currentSession.projectId;
                }
            }

            if ((!isAdminSetting && !(spaceKey && projectKey)) || (isAdminSetting && !spaceKey)) {
                setNoSiteAccess({ key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_1, value: true, errorType: "NO_SITE_ACCESS" });
                return;
            }

            const foundSpace = user.stages.data.find((stage) => stage.id === spaceKey);
            if (!foundSpace) {
                setNoSiteAccess({ key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_2, value: true, errorType: "NO_SITE_ACCESS" });
                return;
            }

            dispatch(setStage(foundSpace));
            updateSettings(foundSpace);

            if (!isAdminSetting) {
                const foundProject = foundSpace?.projects?.find((project) => project.id === projectKey);
                if (!foundProject) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_3,
                        value: true,
                        errorType: "NO_SITE_ACCESS",
                    });
                    return;
                }
                dispatch(setProject(foundProject));
            }
        } else {
            const welcomePath = window.location.pathname.indexOf("/library") > -1 ? "/library/welcome" : "/welcome";
            history.replace(welcomePath);
        }
    }, [user.stages.allProjectsPending]);

    useEffect(() => {
        if (!stage?.id || !project?.id) return;

        const fetchProjectSettings = async () => {
            try {
                const response = await Promise.allSettled([
                    updateProjectSettings(stage, project, "MenuName"),
                    updateProjectSettings(stage, project, "General"),
                ]);
                const rejectedResults = response.filter((result) => result.status === "rejected");
                if (rejectedResults.length > 0) {
                    throw new Error("update project settings failed");
                }
            } catch (e) {
                console.log(e);
            }
        };

        fetchProjectSettings();
    }, [project?.id]);

    useEffect(() => {
        if (!stage || !stage.version || !stage.user) return;
        const locationPathname = window.location.pathname;

        if (
            locationPathname !== "/spaces" &&
            locationPathname !== "/library/spaces" &&
            locationPathname !== "/" &&
            locationPathname !== "/library/" &&
            !isAdminSetting
        ) {
            const userData = stage.user;
            let hasNoSiteAccess = true;
            if (userData && userData.projects) {
                const userProject = userData.projects.filter((p) => p.id === projectId)[0];
                if (stage.id === spaceId && userProject) {
                    const siteAccess = userProject.siteAccess;
                    if (siteAccess) {
                        const mode = siteMode.get();

                        // NOTE: 라이브러리 권한만 있는 사용자가 콘솔로 진입 시 라이브러리로 redirect
                        if (mode === "CONSOLE" && !siteAccess.includes("CONSOLE") && siteAccess.includes("CMS")) {
                            if (location.pathname.indexOf("/library") === -1) {
                                const fullPath = `/library${location.pathname}${location.search}`;
                                window.location.href = `${window.location.origin}${fullPath}`;
                                return;
                            }
                        }

                        hasNoSiteAccess =
                            (mode === "CONSOLE" && siteAccess.indexOf("CONSOLE") === -1) ||
                            (mode === "CMS" && siteAccess.indexOf("CMS") === -1);
                    }
                }
                if (hasNoSiteAccess) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_SITE_ACCESS_4,
                        value: true,
                        errorType: "NO_SITE_ACCESS",
                    });
                }
            }
        }

        if (location.pathname.indexOf(`/admin/spaces/${spaceId}`) !== -1 && !projectId) {
            const userData = stage.user;
            if (userData && !userData.isSpaceAdmin) {
                setNoSiteAccess({
                    key: NO_SITE_ACCESS_KEY.NO_PERMISSION_TO_VIEW_PAGE_1,
                    value: true,
                    errorType: "NO_PERMISSION_TO_VIEW_PAGE",
                });
            }
        } else if (location.pathname.indexOf(`/admin/spaces/${spaceId}/projects/${projectId}`) === 0) {
            const userData = stage.user;
            if (userData && userData.projects) {
                const isAdmin =
                    userData.isSpaceAdmin ||
                    userData.projects.filter((p) => p.id === project.id && p.isAdmin).length > 0;
                if (!isAdmin) {
                    setNoSiteAccess({
                        key: NO_SITE_ACCESS_KEY.NO_PERMISSION_TO_VIEW_PAGE_2,
                        value: true,
                        errorType: "NO_PERMISSION_TO_VIEW_PAGE",
                    });
                }
            }
        }
    }, [stage.user]);

    if (noSiteAccess.value) {
        return <LoadableTokenExpiredModal error={{ type: noSiteAccess.errorType }} onConfirm={closeNoAccessPopup} />;
    }
    if (
        location.pathname.indexOf("/access-token-bridge") !== -1 ||
        location.pathname.indexOf("/snsLogin/youtube") !== -1
    ) {
        return <>{children}</>;
    }
    if (user.pending || user.stages.stagePending || user.stages.allProjectsPending) {
        return <Loader />;
    } else if (user.data != null && user.stages.data !== null) {
        if ((!isAdminSetting && stage.id !== "" && project.id !== "") || (isAdminSetting && stage.id !== "")) {
            return <>{children}</>;
        } else {
            return <Stage />;
        }
    } else if (user.error || user.stages.error) {
        return <InvalidUser />;
    } else {
        return null;
    }
};

export default User;
